import {
  Badge,
  Box,
  Flex,
  ScrollArea,
  Separator,
  Text,
} from "@radix-ui/themes";
import { GondolaChangeGroup } from "@/entities/gondola-change-groups/model/types";
import { InfoItem } from "@/components/info-item";
import { useGondolaChangeGroupsData } from "../hooks/useGondolaChangeGroupsData";
import {
  selectSelectedGondolaChangeGroupId,
  setSelectedGondolaChangeGroupId,
  setSelectedGondolaChangeId,
} from "@/entities/gondola-change-groups";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import { Fragment } from "react/jsx-runtime";
import { useEffect, useCallback } from "react";
import { useTimelineData } from "@/features/annotation/hooks/useTimelineData";
import {
  setGondolaByName,
  setGondolaDropdownValueByName,
} from "@/entities/annotation";
import useCameraHandlers from "@/features/annotation/hooks/useCameraHandlers";
import { countAnnotationsType } from "../lib/helpers";
import { ANNOTATION_ACTIONS } from "@/entities/annotation/model/consts";
import { usePlayer } from "@tezzro/player";

export function GondolaChangeGroupsList() {
  const { filteredData, trackAvatarBindings, getFirstFilteredGondolaChange } =
    useGondolaChangeGroupsData();
  const dispatch = useAppDispatch();
  const selectedGondolaGroupId = useAppSelector(
    selectSelectedGondolaChangeGroupId
  );
  const {
    addGondolaChangesToTimeline,
    removeGondolaChangesFromTimeline,
    addIntersectedProxsToTimeline,
    removeIntersectedProxsFromTimeline,
    highlightGondolaChangesTimeline,
  } = useTimelineData();
  const { handleCameraSelect } = useCameraHandlers();
  const player = usePlayer();

  // Clean up timeline data if selected group is no longer in filtered data
  useEffect(() => {
    if (selectedGondolaGroupId) {
      const selectedGroupStillExists = filteredData.some(
        (group) =>
          group.gondola_change_group_composite_id === selectedGondolaGroupId
      );

      if (!selectedGroupStillExists) {
        removeGondolaChangesFromTimeline(selectedGondolaGroupId);
        removeIntersectedProxsFromTimeline(selectedGondolaGroupId);
        dispatch(setSelectedGondolaChangeGroupId(null));
      }
    }
  }, [
    filteredData,
    selectedGondolaGroupId,
    removeGondolaChangesFromTimeline,
    removeIntersectedProxsFromTimeline,
    dispatch,
  ]);

  const onSelectGondolaChangeGroup = useCallback(
    (gondolaChangeGroup: GondolaChangeGroup) => {
      const nextId =
        selectedGondolaGroupId ===
        gondolaChangeGroup.gondola_change_group_composite_id
          ? null
          : gondolaChangeGroup.gondola_change_group_composite_id;

      if (selectedGondolaGroupId) {
        removeGondolaChangesFromTimeline(selectedGondolaGroupId);
        removeIntersectedProxsFromTimeline(selectedGondolaGroupId);
      }

      if (nextId) {
        const gondolaChanges = gondolaChangeGroup.gondola_changes.map(
          (change) => ({
            id: change.gondola_change_camera_composite_id,
            camera_local_id: change.camera_local_id,
            frame_start_time: change.frame_start_time,
            frame_end_time: change.frame_end_time,
            gondola_name: change.gondola_name,
            gondola_id: change.gondola_id,
          })
        );

        const intersectedProxs = gondolaChangeGroup.intersected_proxs.map(
          (prox, index) => ({
            id: `prox-${prox.track_id}-${prox.hand_id}-${prox.time_start}-${index}`,
            track_id: prox.track_id,
            time_start: prox.time_start,
            time_finish: prox.time_finish,
            hand_id: prox.hand_id,
            gondola_name: prox.gondola_name,
            gondola_id: prox.gondola_id,
          })
        );

        addGondolaChangesToTimeline(gondolaChanges, nextId);
        addIntersectedProxsToTimeline(
          intersectedProxs,
          nextId,
          trackAvatarBindings
        );

        const firstFilteredGondolaChange =
          getFirstFilteredGondolaChange(nextId);

        dispatch(setSelectedGondolaChangeGroupId(nextId));

        if (firstFilteredGondolaChange) {
          const gondolaName = firstFilteredGondolaChange.gondola_name;

          player.seek(
            firstFilteredGondolaChange.frame_start_time,
            firstFilteredGondolaChange.camera_local_id
          );

          dispatch(setGondolaByName(gondolaName || null));
          dispatch(setGondolaDropdownValueByName(gondolaName || null));
          handleCameraSelect(firstFilteredGondolaChange.camera_local_id);

          dispatch(
            setSelectedGondolaChangeId(
              firstFilteredGondolaChange.gondola_change_camera_composite_id
            )
          );

          highlightGondolaChangesTimeline(
            firstFilteredGondolaChange.gondola_change_camera_composite_id
          );
        } else {
          const gondolaName = gondolaChangeGroup.gondola_name;
          const camera = gondolaChangeGroup.unique_camera_local_ids[0];

          player.seek(gondolaChangeGroup.min_frame_start_time, camera);

          dispatch(setGondolaByName(gondolaName || null));
          dispatch(setGondolaDropdownValueByName(gondolaName || null));
          handleCameraSelect(camera);
        }
      } else {
        dispatch(setSelectedGondolaChangeId(null));
        highlightGondolaChangesTimeline(null);
        dispatch(setSelectedGondolaChangeGroupId(nextId));
      }
    },
    [
      selectedGondolaGroupId,
      removeGondolaChangesFromTimeline,
      removeIntersectedProxsFromTimeline,
      addGondolaChangesToTimeline,
      addIntersectedProxsToTimeline,
      trackAvatarBindings,
      handleCameraSelect,
      highlightGondolaChangesTimeline,
      getFirstFilteredGondolaChange,
      dispatch,
      player,
    ]
  );

  return (
    <Flex direction="column" flexGrow="1" flexBasis="1">
      <Flex align="center" justify="between" m="2">
        <Text size="2" weight="bold" as="p">
          Gondola Changes Groups{" "}
          {filteredData.length > 0 && `(${filteredData.length})`}
        </Text>
      </Flex>
      <Separator size="4" />
      <ScrollArea type="auto" className="h-0 flex-grow">
        {filteredData.length === 0 ? (
          <Box p="4">
            <Text size="2" color="gray">
              No gondola change groups match the current filters
            </Text>
          </Box>
        ) : (
          filteredData.map((gondolaChangeGroup: GondolaChangeGroup) => (
            <Fragment
              key={gondolaChangeGroup?.gondola_change_group_composite_id}
            >
              <Box
                className={`p-2 rounded  ${
                  selectedGondolaGroupId ===
                  gondolaChangeGroup?.gondola_change_group_composite_id
                    ? "bg-blue-100 border border-blue-300"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => onSelectGondolaChangeGroup(gondolaChangeGroup)}
              >
                <InfoItem
                  label="Gondola name"
                  value={gondolaChangeGroup?.gondola_name}
                />
                <InfoItem
                  label="Gondola ID"
                  value={gondolaChangeGroup?.gondola_id}
                />
                <InfoItem
                  label="Unique camera local IDs"
                  value={gondolaChangeGroup?.unique_camera_local_ids.join(", ")}
                />
                <InfoItem
                  label="Min frame start time"
                  value={gondolaChangeGroup?.min_frame_start_time}
                />
                <InfoItem
                  label="Max frame end time"
                  value={gondolaChangeGroup?.max_frame_end_time}
                />
                <InfoItem
                  label="Gondola change count"
                  customValue={
                    gondolaChangeGroup.gondola_changes?.length <= 1 ? (
                      <Badge color="red">
                        {gondolaChangeGroup.gondola_changes?.length}
                      </Badge>
                    ) : (
                      gondolaChangeGroup.gondola_changes?.length
                    )
                  }
                />
                <InfoItem
                  label="Intersected annotation TAKE count"
                  value={countAnnotationsType(
                    gondolaChangeGroup.annotations,
                    ANNOTATION_ACTIONS.ADD
                  )}
                />
                <InfoItem
                  label="Intersected annotation PUT count"
                  value={countAnnotationsType(
                    gondolaChangeGroup.annotations,
                    ANNOTATION_ACTIONS.REMOVE
                  )}
                />
                <InfoItem
                  label="Intersected annotations count"
                  customValue={
                    gondolaChangeGroup.annotations?.length > 0 ? (
                      <Badge color="green">
                        {gondolaChangeGroup.annotations?.length}
                      </Badge>
                    ) : (
                      `${gondolaChangeGroup.annotations?.length}`
                    )
                  }
                />
              </Box>
              <Separator size="4" />
            </Fragment>
          ))
        )}
      </ScrollArea>
    </Flex>
  );
}
